import type { Lab } from '@domain/models/lab.interface';
import type { ScanConnection } from '@domain/models/scan-connection.interface';
import type { IScanConnectionRepository } from '@domain/data-contracts/scan-connection-repository.interface';

export interface LabConnectionRow {
  readonly lab: Lab;
  readonly connections: readonly ScanConnection[];
}

const CONCURRENCY_LIMIT = 10;

/**
 * Fan-out: for each lab, fetches its scan connections from the
 * Gateways API in parallel chunks of {@link CONCURRENCY_LIMIT}.
 * A per-lab failure is swallowed (returns an empty connections
 * array) so one failing org never blocks the others.
 */
export async function aggregatePlatformConnections(
  labs: readonly Lab[],
  scanConnectionRepository: IScanConnectionRepository,
  signal?: AbortSignal,
): Promise<readonly LabConnectionRow[]> {
  const results: LabConnectionRow[] = [];

  for (let i = 0; i < labs.length; i += CONCURRENCY_LIMIT) {
    const chunk = labs.slice(i, i + CONCURRENCY_LIMIT);
    const settled = await Promise.all(
      chunk.map(async (lab): Promise<LabConnectionRow> => {
        try {
          const connections = await scanConnectionRepository.listForOrg(lab.id, lab.name, signal);
          return { lab, connections };
        } catch {
          return { lab, connections: [] };
        }
      }),
    );
    results.push(...settled);
  }

  return results;
}
